/**
 * Created by Abner Sui on 09/10/2019
 * ------ maintenance history ------
 * Modified by Simon Zhao on 9/1/2021 added the selection state.
 * Modified by Simon Zhao on 08/25/2022 removed the code of stopping propagation of click event for file-info fields
 */

import { Component, OnInit, Input, OnDestroy, ChangeDetectionStrategy, ChangeDetectorRef } from '@angular/core';
import { debounceTime } from 'rxjs/operators';
import { SlideSheetActionTypes, SlideSheetEventTypes } from './../../slide-sheet/slide-sheet.model';
import { Task, Field, TaskType, FieldType } from '../../../tamalelibs/models/workflow.model';
import { BriefConfig } from '../../../widgets/widgets.model';
import { KanbanCardAction } from './kanban-card.model';
import { EntityBrief } from '../../../tamalelibs/models/entity-brief.model';
import { WorkflowService } from '../../../tamalelibs/services/workflow.service';
import { CurrencyPipe } from '@angular/common';
import { UtilsService } from '../../../tamalelibs/services/utils.service';
import { Store } from '@ngrx/store';
import { Subscription } from 'rxjs-compat';
import { SlideSheetService } from '../../../services/slide-sheet.service';
import { WorkflowKanbanScreenStateService } from '../workflow-kanban-screen-state/workflow-kanban-screen-state.service';
import { Entity } from '../../../tamalelibs/models/entity.model';
import { ArrayHelperService } from '../../../tamalelibs/services/array-helper.service';
import { ControlType } from '../../../tamalelibs/models/template-control.model';
@Component({
    selector: 'kanban-task-card',
    templateUrl: './kanban-task-card.component.html',
    styleUrls: ['./kanban-card.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class KanbanTaskCardComponent implements OnInit, OnDestroy {

    @Input() config: BriefConfig;

    @Input() task: Task;

    assigneeCount: number;
    checkLists: any[] = [];
    displayAssignee: Array<EntityBrief>;
    displayApproveTaskStatus: string;
    fields: Array<Field> = [];
    /**
    * a flag indicating whether current task card is selected.
    */
    get isSelected() {
        return this.task && !!this._kanbanStateService.selectedTaskId && this._kanbanStateService.selectedTaskId === this.task.id;
    }
    primaryEntityName: string;

    private _destroySubscriptions: Array<Subscription> = [];

    constructor(
        private _workflowService: WorkflowService,
        private _currencyPipe: CurrencyPipe,
        private _detectChange: ChangeDetectorRef,
        private _utils: UtilsService,
        private _slideSheetService: SlideSheetService,
        private _kanbanStateService: WorkflowKanbanScreenStateService
    ) { }

    ngOnInit() {
        this.displayAssignee = this.task.displayAssignee.filter(ass => ass.name);
        this.task.taskDefinition?.template?.contains.forEach(item => {
            item.forEach(control => {
                // get all check list to handle the display in the view
                if (control.type === ControlType.CHECK_LIST) {
                    this.checkLists.push(control);
                }
            });
        });
        ArrayHelperService.sort(this.displayAssignee, 'shortName');
        ArrayHelperService.sort(this.task.assignee, 'shortName');
        this.assigneeCount = this.displayAssignee.length;

        if (this.task.processInstance && this.task.processInstance.primaryEntity && this.task.processInstance.primaryEntity.shortName) {
            this.primaryEntityName = this.task.processInstance.primaryEntity.shortName;
        }
        this._workflowService.attachFieldConfigFromTemplate(this.task);
        // the rich editor field should not show in kanban card.
        this.fields = this._workflowService.getDisplayFieldsOnCard(this.task.taskDefinition);
        this.displayApproveTaskStatus = this._getStatus();
        this._destroySubscriptions.push(
            this._slideSheetService.slideSheetActionSubject$.pipe(debounceTime(100)).subscribe(action => {
                if (action.type === SlideSheetActionTypes.TASK) {
                    if (action.payload && action.payload.task && action.payload.task.id === this.task.id) {
                        this._kanbanStateService.selectedTaskId = this.task.id;
                    }
                    this._detectChange.detectChanges();
                }
            }),
            this._slideSheetService.slideSheetActionSubject$.subscribe(action => {
                if (action.type === SlideSheetActionTypes.CLOSE) {
                    this._kanbanStateService.selectedInstanceId = undefined;
                    this._kanbanStateService.selectedTaskId = undefined;
                    this._detectChange.detectChanges();
                }
            }),
            this._slideSheetService.slideSheetFeedbackSubject$.subscribe(res => {
                if (res.type === SlideSheetEventTypes.SLIDE_CLOSING) {
                    this._kanbanStateService.selectedTaskId = undefined;
                    this._kanbanStateService.selectedInstanceId = undefined;
                    this._detectChange.detectChanges();
                }
            }));
    }

    ngOnDestroy() {
        this._destroySubscriptions.forEach(item => item.unsubscribe());
    }

    getAbbrName(assignee: EntityBrief): string {
        return this._workflowService.getAbbrNameForOneEntity(assignee);
    }

    getAssignee(): Array<EntityBrief> {
        if (this.task.owner) {
            return [this.task.owner];
        } else if (this.assigneeCount > 9) {
            return this.displayAssignee.slice(0, 9);
        } else {
            return this.displayAssignee;
        }
    }

    getBackgroundColorById(id: string): string {
        return this._utils.getBackgroundColorById(id);
    }

    getDisplayValueForEntities(entities: Array<EntityBrief>): string {
        return this._workflowService.getDisplayValueForEntities(entities);
    }

    getEntities(): Array<EntityBrief> {
        return this._workflowService.getEntitiesFromFields(this.fields);
    }

    getFormatForNumber(field: Field) {
        return this._workflowService.getFormatForNumber(field);
    }

    getNumberStr(): string {
        const result: Array<string> = [];
        this.fields.forEach(f => {
            if (f.value && (f.fieldDefinition.type === FieldType.NUMBER || f.fieldDefinition.type === FieldType.CURRENCY)) {
                const oneNumStr = this._currencyPipe.transform(f.value, 'CAD', 'symbol-narrow', '1.0-0');
                result.push(f.fieldDefinition.name + ' ' + oneNumStr);
            }
        });
        return result.join(', ');
    }

    getTaskName(): string {
        const result: string = this.task.name;
        return result;
    }

    getZIndex(index: number): number {
        return this.assigneeCount - index;
    }

    hasMoreAssignee(): boolean {
        return !this.task.owner && this.assigneeCount > 9;
    }

    /**
     * obtains the relative url to zoom the corresponding entity.
     * @param v the entity field value
     * @returns a relative url to zoom the corresponding entity.
     */
    getEntityRelativeLink(v: Entity) {
        return WorkflowService.getEntityRelativeLink(v);
    }

    /**
     * obtains the check list field brief info.
     * @param v the value of a check list field.
     * @returns a progress message of a check list field, like 2/5, the numerator is the amout of checked items and the denominator is the total number of check items.
    */
    getCheckListFieldInfo(v: Field, checkLists?: any[]) {
        return WorkflowService.getFieldBriefContent(v, checkLists);
    }

    /**
     * determines whether the given value is an array.
     * @param v the given field value.
     * @returns a flag indicating whether the given value is an array.
     */
    isEntityArray(v: Array<Entity> | Entity) {
        return Array.isArray(v);
    }

    /**
     * determines whether the bottom line is visible by the existence of corresponding content.
     * @returns a flag indicating whether the bottom line should appear
     */
    isBottomLineVisible() {
        return this.task && ((this.task.comments && this.task.comments.length > 0) || (this.task.attachments && this.task.attachments.length > 0)
            || this.task.dueDate);
    }

    onMeatBall(event): void {
        this.config.feedbackSubject$.next({
            type: KanbanCardAction.ON_MEAT_BALL,
            payload: {
                task: this.task,
                event: event,
            },
        });
    }

    private _getStatus(): string {
        // TODO, for multiple approval, if current user is assignee, show current user's status, if not assignee, show whole status
        return this._workflowService.getDisplayValueForTaskStatus(this.task);
    }
}
